/**
 * React Query hooks for session operations
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '../../lib/queryKeys'
import * as sessionsService from '../../services/sessions.service'
import { useToast } from '../../context/ToastContext'
import type { Session } from '../../types'
import type { DocumentSnapshot } from 'firebase/firestore'

// ============================================
// Query Hooks
// ============================================

/**
 * Hook for fetching a single session
 */
export function useSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sessions.detail(sessionId ?? ''),
    queryFn: () => sessionsService.getSession(sessionId!),
    enabled: !!sessionId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for fetching sessions list (non-paginated)
 */
export function useSessions(filters: {
  userId?: string
  startDate?: string
  endDate?: string
  status?: Session['status']
  limit?: number
} = {}) {
  return useQuery({
    queryKey: queryKeys.sessions.list(filters),
    queryFn: () => sessionsService.getSessions(filters),
    staleTime: 1000 * 60 * 2,
  })
}

/**
 * Hook for fetching sessions with infinite scroll pagination
 */
export function useSessionsPaginated(filters: {
  userId?: string
  startDate?: string
  endDate?: string
  status?: Session['status']
} = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.sessions.list(filters),
    queryFn: async ({ pageParam }) => {
      return sessionsService.getSessionsPaginated({
        ...filters,
        cursor: pageParam as DocumentSnapshot | null,
      })
    },
    initialPageParam: null as DocumentSnapshot | null,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastDoc : undefined),
    staleTime: 1000 * 60 * 2,
  })
}

/**
 * Hook for fetching today's active session for a user
 */
export function useTodayActiveSession(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sessions.active(userId ?? ''),
    queryFn: () => sessionsService.getTodayActiveSession(userId!),
    enabled: !!userId,
    staleTime: 1000 * 30, // 30 seconds - more frequent for active session
    refetchInterval: 1000 * 30, // Auto-refetch every 30 seconds
  })
}

/**
 * Hook for fetching today's sessions (dashboard)
 */
export function useTodaysSessions(date: string) {
  return useQuery({
    queryKey: queryKeys.sessions.today(date),
    queryFn: () => sessionsService.getSessions({ startDate: date, endDate: date }),
    staleTime: 1000 * 60 * 1, // 1 minute
  })
}

/**
 * Hook for fetching a user's sessions
 */
export function useUserSessions(userId: string, filters: {
  startDate?: string
  endDate?: string
  status?: Session['status']
} = {}) {
  return useQuery({
    queryKey: queryKeys.sessions.userSessions(userId),
    queryFn: () => sessionsService.getSessions({ userId, ...filters }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook for clocking in
 */
export function useClockIn() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: sessionsService.clockIn,
    onSuccess: (newSession, variables) => {
      // Optimistically update cache with new session
      queryClient.setQueryData(
        queryKeys.sessions.active(variables.userId),
        newSession
      )
      toast.success(t('clock.clockedInSuccess'))
    },
    onSettled: () => {
      // Batch invalidations in onSettled to ensure they happen after
      // the mutation completes (success or error)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.lists() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.adminStats })
      ])
    },
    onError: (error: Error) => {
      toast.error(error.message || t('clock.failedToClockIn'))
    },
  })
}

/**
 * Hook for clocking out
 */
export function useClockOut() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: sessionsService.clockOut,
    onSuccess: () => {
      toast.success(t('clock.clockedOutSuccess'))
    },
    onSettled: () => {
      // Batch all invalidations together
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.adminStats })
      ])
    },
    onError: (error: Error) => {
      toast.error(error.message || t('clock.failedToClockOut'))
    },
  })
}

/**
 * Hook for starting a break
 */
export function useStartBreak() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: sessionsService.startBreak,
    onSuccess: () => {
      toast.success(t('break.breakStarted'))
    },
    onSettled: (_, __, variables) => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.detail(variables.sessionId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all })
      ])
    },
    onError: (error: Error) => {
      toast.error(error.message || t('break.failedToStartBreak'))
    },
  })
}

/**
 * Hook for ending a break
 */
export function useEndBreak() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: sessionsService.endBreak,
    onSuccess: () => {
      toast.success(t('break.breakEnded'))
    },
    onSettled: (_, __, variables) => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.detail(variables.sessionId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all })
      ])
    },
    onError: (error: Error) => {
      toast.error(error.message || t('break.failedToEndBreak'))
    },
  })
}

/**
 * Hook for approving a session (admin only)
 */
export function useApproveSession() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ sessionId, adminId }: { sessionId: string; adminId: string }) =>
      sessionsService.approveSession(sessionId, adminId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.detail(variables.sessionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.lists() })
      toast.success(t('session.approved'))
    },
    onError: (error: Error) => {
      toast.error(error.message || t('errors.session.approveFailed'))
    },
  })
}

/**
 * Hook for creating manual entry (admin only)
 */
export function useCreateManualEntry() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: sessionsService.createManualEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all })
      toast.success(t('session.manualEntryCreated'))
    },
    onError: (error: Error) => {
      toast.error(error.message || t('errors.session.createFailed'))
    },
  })
}

// ============================================
// Admin Dashboard Stats Hook
// ============================================

/** Options for useAdminDashboardStats hook */
interface AdminDashboardStatsOptions {
  /** Whether to enable the query (default: true) */
  enabled?: boolean
}

/**
 * Hook for fetching admin dashboard statistics
 * Combines multiple queries into one for efficiency
 */
export function useAdminDashboardStats(options: AdminDashboardStatsOptions = {}) {
  const { enabled = true } = options

  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]

  // Calculate start of week (Monday)
  const dayOfWeek = today.getDay()
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - daysFromMonday)
  const weekStartStr = weekStart.toISOString().split('T')[0]

  return useQuery({
    queryKey: queryKeys.adminStats,
    enabled,
    queryFn: async () => {
      const [pendingUsers, todaysSessions, weekSessions, activeWorkersCount, totalWorkersCount, flaggedCount, allUsers] = await Promise.all([
        import('../../services/users.service').then(m => m.getPendingUsers()),
        sessionsService.getSessions({ startDate: todayStr, endDate: todayStr }),
        sessionsService.getSessions({ startDate: weekStartStr, endDate: todayStr }),
        import('../../services/users.service').then(m => m.getActiveWorkersCount()),
        import('../../services/users.service').then(m => m.getTotalWorkersCount()),
        sessionsService.getFlaggedSessionsCount(todayStr),
        import('../../services/users.service').then(m => m.getUsers({ role: 'employee' })),
      ])

      const totalMinutesToday = todaysSessions.reduce(
        (sum, s) => sum + (s.totalWorkMinutes || 0),
        0
      )

      const totalMinutesWeek = weekSessions.reduce(
        (sum, s) => sum + (s.totalWorkMinutes || 0),
        0
      )

      // Count expiring certificates (within 30 days)
      const now = new Date()
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
      let expiringCerts = 0

      for (const user of allUsers) {
        const certs = (user as any).certificates
        if (certs) {
          if (certs.manualHandling?.expirationDate) {
            const expDate = new Date(certs.manualHandling.expirationDate)
            if (expDate <= thirtyDaysFromNow) expiringCerts++
          }
          if (certs.safetyPass?.expirationDate) {
            const expDate = new Date(certs.safetyPass.expirationDate)
            if (expDate <= thirtyDaysFromNow) expiringCerts++
          }
        }
      }

      return {
        totalWorkers: totalWorkersCount,
        activeWorkers: activeWorkersCount,
        pendingApprovals: pendingUsers.length,
        todaySessions: todaysSessions.length,
        flaggedSessions: flaggedCount,
        totalHoursToday: Math.round((totalMinutesToday / 60) * 10) / 10,
        totalHoursWeek: Math.round((totalMinutesWeek / 60) * 10) / 10,
        expiringCertificates: expiringCerts,
        recentSessions: todaysSessions.slice(0, 5),
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - use cached data while fresh
    refetchInterval: 1000 * 60 * 2, // Auto-refetch every 2 minutes in background
  })
}
